"use strict"

import React, { useEffect, useState } from "react"
import { Modal } from "react-bootstrap"

import { isLTPM, withApplicationConfig, injectIntl } from "../../components/shared_components_registration"
import { getObjectsList } from "../../Loaders"
import { logToServer } from "../../ErrorBoundary"

const registration = {
  logout_title: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "general.you.will.be.logged.out",
          defaultMessage: "You will be logged out shortly due to inactivity",
        })
      : "You will be logged out shortly due to inactivity",
  logout_msg: (intl, value) => {
    const zeroPadNumber = (num) => (num < 10 ? `0${num}` : `${num}`)
    const val = value + 1
    const displayNumber = `00:${zeroPadNumber(Math.floor(val / 60))}:${zeroPadNumber(Math.floor(val % 60))}`
    return isLTPM
      ? intl.formatMessage(
          { id: "general.logging.out.in", defaultMessage: `Logging out in ${displayNumber}` },
          { countdown: displayNumber },
        )
      : `Logging out in ${displayNumber}`
  },
}

function LogoutTimer({ config, intl }) {
  const [countdown, setCountdown] = useState(25 * 60) // Default to 25 minutes
  const [show, setShow] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [resettingTimer, setResettingTimer] = useState(false)
  const [isAutoLogoutEnabled, setIsAutoLogoutEnabled] = useState(true)
  const [timeLeftToWarning, setTimeLeftToWarning] = useState(15 * 60) // Default to 15 minutes
  const [timeLeftToAutoLogout, setTimeLeftToAutoLogout] = useState(25 * 60) // Default to 25 minutes
  const [checkFrequency, setCheckFrequency] = useState(5000) // Default to 5 seconds
  const [sessionRefreshFrequency, setSessionRefreshFrequency] = useState(5 * 60 * 1000) // Default to 5 minutes

  // Update hardcoded values with django settings as soon as the settings are loaded
  useEffect(() => {
    if (config?.settings && !Object.values(config?.settings).includes(undefined)) {
      const expiry = config.settings.SESSION_EXPIRE_SECONDS
      setTimeLeftToAutoLogout(() => expiry)
      setCountdown(() => expiry)
      setTimeLeftToWarning(() => expiry / 4)
      setCheckFrequency(() => (expiry / 8) * 1000)
      setSessionRefreshFrequency(() => (expiry / 10) * 1000)
      setIsAutoLogoutEnabled(() => config.settings.SESSION_EXPIRE_AFTER_LAST_ACTIVITY)
    }
  }, [config])

  useEffect(() => {
    const nonIdleEvents = ["click", "keypress", "scroll", "mousemove"]

    const makeIsActiveTrue = () => {
      setIsActive(() => true)
    }
    const resetCountdown = () => {
      setShow(() => false)
      setResettingTimer(() => true)
    }

    nonIdleEvents.map((item) => {
      window.addEventListener(item, makeIsActiveTrue)
      window.addEventListener(item, resetCountdown)
    })
    return () => {
      nonIdleEvents.map((item) => {
        window.removeEventListener(item, makeIsActiveTrue)
        window.removeEventListener(item, resetCountdown)
      })
    }
  }, [])

  useEffect(() => {
    const checkIsActive = () => {
      isActive ? resetIsActive() : null
    }
    const resetIsActive = () => {
      getObjectsList("/users/list/refresh/")
        .then((_) => {})
        .catch((_) => {
          let err = "Session refresh failed."
          logToServer(err)
          console.error(err)
        })
      setIsActive(() => false)
    }
    const interval = setInterval(checkIsActive, sessionRefreshFrequency)
    return () => clearInterval(interval)
  }, [isActive])

  // This useEffect drives a countdown timer that decreases every `checkFrequency`
  useEffect(() => {
    let timeoutId

    if (resettingTimer) {
      setCountdown(() => timeLeftToAutoLogout)
      setResettingTimer(() => false)
    } else if (isAutoLogoutEnabled) {
      // Enable the countdown timer if auto logout is enabled
      if (countdown >= 0) {
        // Increase update frequency to every second if time left is in the 'warning' window
        const freq = countdown <= timeLeftToWarning ? 1000 : checkFrequency
        timeoutId = setTimeout(() => setCountdown((prevCountdown) => prevCountdown - freq / 1000), freq)
        // shows modal if time left is in the 'warning' window
        if (countdown <= timeLeftToWarning && !show) {
          setShow(() => true)
        }
      } else {
        /*
          Log user out if countdown timer has run out.

          The timer we keep track of in the UI is not always in-sync with what Django sessions
          is doing so if the UI countdown runs out we specifically ask to log the user out and then
          refresh the current location to prompt a forbidden/log-in page showing. Trying to directly alter
          the location.href was doing weird things with redirects sometimes (e.g. redirect from the logout
          page to the logout page AGAIN, etc.). Easier to just take control, log the user out, and
          refresh the page to show what has happened to the user.
        */
        getObjectsList("/users/list/logout")
          .then((_) => location.reload())
          .catch((_) => location.reload())
      }
    } else {
      // Otherwise, just let the countdown timer sit idle
      setCountdown((prevCountdown) => prevCountdown)
    }

    return () => timeoutId && clearTimeout(timeoutId)
  }, [countdown, resettingTimer])

  return (
    <>
      {show && (
        <div className={"logout-timer"}>
          <>
            <Modal show={show} onHide={() => setShow(() => false)}>
              <Modal.Header closeButton>
                <Modal.Title>{registration.logout_title(intl)}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>{registration.logout_msg(intl, countdown)}</p>
              </Modal.Body>
            </Modal>
          </>
        </div>
      )}
    </>
  )
}

export default withApplicationConfig(injectIntl(LogoutTimer))
